import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const SalesPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    sales_overview: false,
    customers: false,
    invoices: false,
    price_lists: false
  });
  
  // Sales pages mapping
  const salesPages = ['sales_overview', 'customers', 'invoices', 'price_lists'];
  const pageNameMapping = {
    'sales_overview': 'CRM Overview Page',
    'customers': 'Customers Page',
    'invoices': 'Invoices Page',
    'price_lists': 'Price Lists Page'
  };
  const pageIconMapping = {
    'sales_overview': <GroupsIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'customers': <PeopleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'invoices': <ReceiptIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'price_lists': <LocalOfferIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderSalesPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    
    let pagePermissions = [];
    
    if (pageKey === 'customers') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('sales.customers'));
    } else if (pageKey === 'sales_overview') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('sales.overview'));
    } else {
      pagePermissions = permissions.filter(p => p.name && p.name.includes(`sales.${pageKey}`));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()} page
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPerm && pageKey !== 'sales_overview' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission */}
            {deletePerm && pageKey !== 'sales_overview' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {salesPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderSalesPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default SalesPermissions;
